import { createSelector } from 'redux-orm';

import orm from '../orm';
import { pathSelector } from './path';
import { isLocalId } from '../utils/local-id';

export const devicesSelector = createSelector(orm, ({ Device, DeviceModel, Channel }) => {
  return Device.getOrderedQuerySet()
    .toRefArray()
    .map((device) => {
      let modelModel;
      let devStatus = device.status;
      if (device.modelId) modelModel = DeviceModel.withId(device.modelId);

      /* In caseof logicDevice, recalc the status base on device list */
      if (device.logicDevice === true) {
        for (let i = 0; i < device.deviceIds.length; i += 1) {
          const deviceItem = Device.withId(device.deviceIds[i]);

          if (deviceItem) {
            if (deviceItem.status === 0) {
              devStatus = 0;
              break;
            } else {
              devStatus = deviceItem.status;
            }
          }
        }
      }
      return {
        ...device,
        model: modelModel?.ref,
        channel: Channel.withId(device.channelId)?.ref,
        status: devStatus,
      };
    });
});

export const makeDeviceByIdSelector = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Device }, id) => {
      const deviceModel = Device.withId(id);

      if (!deviceModel) {
        return deviceModel;
      }

      return {
        ...deviceModel.ref,
        line: deviceModel.line?.ref,
        site: deviceModel.site?.ref,
        factory: deviceModel.site?.factory?.ref,
        coverUrl: deviceModel.coverAttachment && deviceModel.coverAttachment.coverUrl,
        isPersisted: !isLocalId(id),
      };
    },
  );

export const makeNotificationsTotalByDeviceIdSelector = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Device }, id) => {
      const deviceModel = Device.withId(id);

      if (!deviceModel) {
        return deviceModel;
      }

      return deviceModel.getUnreadNotificationsQuerySet().count();
    },
  );

export const makeLastActionIdByDeviceIdSelector = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Device }, id) => {
      const deviceModel = Device.withId(id);

      if (!deviceModel) {
        return deviceModel;
      }

      const lastActionModel = deviceModel.getOrderedInDeviceActionsQuerySet().last();

      return lastActionModel && lastActionModel.id;
    },
  );

export const makeLabelsByDeviceIdSelector = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Device }, id) => {
      const deviceModel = Device.withId(id);

      if (!deviceModel) {
        return deviceModel;
      }

      return deviceModel.labels.toRefArray();
    },
  );

export const makeUsersByDeviceIdSelector = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Device }, id) => {
      const deviceModel = Device.withId(id);

      if (!deviceModel) {
        return deviceModel;
      }

      return deviceModel.users.toRefArray();
    },
  );

export const makeAlarmsByDeviceIdSelector = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Device }, id) => {
      const deviceModel = Device.withId(id);

      if (!deviceModel) {
        return deviceModel;
      }

      return deviceModel.alarms.toRefArray();
    },
  );

export const currentDeviceSelector = createSelector(
  orm,
  (state) => pathSelector(state).deviceId,
  ({ Device }, id) => {
    if (!id) {
      return id;
    }

    const deviceModel = Device.withId(id);

    if (!deviceModel) {
      return deviceModel;
    }

    return deviceModel.ref;
  },
);

export const usersForCurrentDeviceSelector = createSelector(
  orm,
  (state) => pathSelector(state).deviceId,
  ({ Device }, id) => {
    if (!id) {
      return id;
    }

    const deviceModel = Device.withId(id);

    if (!deviceModel) {
      return deviceModel;
    }

    return deviceModel.users.toRefArray();
  },
);

export const labelsForCurrentDeviceSelector = createSelector(
  orm,
  (state) => pathSelector(state).deviceId,
  ({ Device }, id) => {
    if (!id) {
      return id;
    }

    const deviceModel = Device.withId(id);

    if (!deviceModel) {
      return deviceModel;
    }

    return deviceModel.labels.toRefArray();
  },
);

export const alarmsForCurrentDeviceSelector = createSelector(
  orm,
  (state) => pathSelector(state).deviceId,
  ({ Device }, id) => {
    if (!id) {
      return id;
    }

    const deviceModel = Device.withId(id);

    if (!deviceModel) {
      return deviceModel;
    }

    return deviceModel
      .getOrderedAlarmsQuerySet()
      .toRefArray()
      .map((alarm) => ({
        ...alarm,
        isPersisted: !isLocalId(alarm.id),
      }));
  },
);

export const attachmentsForCurrentDeviceSelector = createSelector(
  orm,
  (state) => pathSelector(state).deviceId,
  ({ Device }, id) => {
    if (!id) {
      return id;
    }

    const deviceModel = Device.withId(id);

    if (!deviceModel) {
      return deviceModel;
    }

    return deviceModel
      .getOrderedAttachmentsQuerySet()
      .toRefArray()
      .map((attachment) => ({
        ...attachment,
        isCover: attachment.id === deviceModel.coverAttachmentId,
        isPersisted: !isLocalId(attachment.id),
      }));
  },
);

export const notificationIdsForCurrentDeviceSelector = createSelector(
  orm,
  (state) => pathSelector(state).deviceId,
  ({ Device }, id) => {
    if (!id) {
      return id;
    }

    const deviceModel = Device.withId(id);

    if (!deviceModel) {
      return deviceModel;
    }

    return deviceModel
      .getUnreadNotificationsQuerySet()
      .toRefArray()
      .map((notification) => notification.id);
  },
);

export const deviceByIdSelector = makeDeviceByIdSelector();

export const notificationsTotalByDeviceIdSelector = makeNotificationsTotalByDeviceIdSelector();

export const lastActionIdByDeviceIdSelector = makeLastActionIdByDeviceIdSelector();

export const labelsByDeviceIdSelector = makeLabelsByDeviceIdSelector();

export const usersByDeviceIdSelector = makeUsersByDeviceIdSelector();

export const alarmsByDeviceIdSelector = makeAlarmsByDeviceIdSelector();
